import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DomainProviderResult {
  lockedProvider: {
    id: string;
    name: string;
    slug: string;
    image_url: string | null;
    custom_domain: string | null;
    display_name: string | null;
    brand_primary_color: string | null;
    brand_secondary_color: string | null;
    brand_font: string | null;
    brand_header_text_color: string | null;
    brand_header_font_weight: string | null;
    brand_header_icon_text_color: string | null;
    brand_header_button_text_color: string | null;
    brand_header_letter_spacing: string | null;
  } | null;
  isDomainLocked: boolean;
  isLoading: boolean;
}

export const useDomainProvider = (): DomainProviderResult => {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";

  // Skip query for main domain or localhost
  const isMainDomain =
    hostname === "wellsuites.co" ||
    hostname === "www.wellsuites.co" ||
    hostname.endsWith(".lovable.app") ||
    hostname === "localhost" ||
    hostname === "127.0.0.1";

  const { data: lockedProvider, isLoading } = useQuery({
    queryKey: ["domain-provider", hostname],
    queryFn: async () => {
      if (isMainDomain) return null;

      const { data, error } = await supabase
        .from("providers")
        .select(`
          id, 
          name, 
          slug, 
          image_url, 
          custom_domain,
          display_name,
          brand_primary_color,
          brand_secondary_color,
          brand_font,
          brand_header_text_color,
          brand_header_font_weight,
          brand_header_icon_text_color,
          brand_header_button_text_color,
          brand_header_letter_spacing
        `)
        .eq("custom_domain", hostname)
        .maybeSingle();

      if (error) {
        console.error("Error fetching domain provider:", error);
        return null;
      }

      return data;
    },
    enabled: !isMainDomain,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  return {
    lockedProvider: lockedProvider || null,
    isDomainLocked: !isMainDomain && !!lockedProvider,
    isLoading: !isMainDomain && isLoading,
  };
};
